import { isModuleSimpleTextSection, isModuleListSection } from "@calcom/contentful/type-guards";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Button } from "@calcom/ui";
import { Markdown } from "@lib/Markdown";
import { BookingSection } from "@calcom/features/bookings/components/BookingSection";
import { ExpertsList } from "@calcom/features/experts/components/ExpertsList";
import { getExperts } from "@calcom/features/experts/lib/getExperts";
import { Hero } from "@components/ui/Hero";
import NextImage from "next/image";
import Head from "next/head";

import type { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import { getNextAvailableSlotForExpert } from "helpers/getNextAvailableSlotForExpert";
import { fetchExpertAndBookingData, fetchScheduleData } from "@lib/experts/api";

export type ExpertPageProps = InferGetStaticPropsType<typeof getStaticProps>;

function ExpertPage({ expert, booking, experts, slots }: ExpertPageProps) {
  const { t } = useLocale();

  if (!expert) {
    return null;
  }

  const expertsNextAvailableSlot = getNextAvailableSlotForExpert(experts, slots);
  const randomSort = () => Math.random() - 0.5;

  const MAX_OTHER_EXPERTS = 2;
  const isNotCurrentExpert = (candidateExpert: typeof expert) => candidateExpert.id !== expert.id;
  const otherExperts = experts
    .filter((e) => isNotCurrentExpert(e))
    .sort(randomSort)
    .slice(0, MAX_OTHER_EXPERTS);

  return (
    <>
      <Head>
        <title>NoA Consultations</title>
        <meta
          name="description"
          content="This is NoA Consultations expert page, where you can find more informations about our expert"
        />
      </Head>

      <Hero color="gray">
        <div className="container relative flex flex-col items-center md:flex-row">
          <div className="max-w-2xl pt-10 md:py-32">
            <h1 className="text-5xl md:text-6xl">{expert.name}</h1>
            <p className="mt-4 text-2xl leading-normal">{expert.position}</p>
            <p className="my-4 text-xl leading-normal">{expert.bio}</p>
            <p className="my-4 text-xl leading-normal">
              Running time: 60 minutes
              <br />
              Price: <span className="line-through">£120</span>{" "}
              <span className="font-bold">NOW FREE (limited time offer)</span>
            </p>
            <Button size="2xl" href="#book-now">
              {t("book")}
            </Button>
          </div>
          <NextImage
            // @ts-expect-error contentful types are wrong
            src={expert.picture?.fields?.file?.url || ""}
            alt={`Photo of ${expert.name ?? "the expert"}`}
            width={300}
            height={300}
            sizes="(max-width: 768px) 100w, (max-width: 1200px) 200w, 300w"
            className="max-w-80 aspect-square h-auto object-cover md:ml-auto md:w-[50%] md:max-w-[631px] md:self-end"
          />
        </div>
      </Hero>
      <main>
        {expert.longBio?.map((item) => {
          if (isModuleSimpleTextSection(item)) {
            return (
              <div
                key={item.sys.id}
                className="container flex flex-col gap-6 py-12 md:grid md:md:grid md:grid-cols-12 md:gap-10 md:py-32">
                <div className="col-span-12 md:col-span-5">
                  <Markdown>{item?.fields?.sectionHeader?.fields?.header}</Markdown>
                </div>
                {item?.fields?.text && typeof item?.fields?.text === "string" && (
                  <div className="prose col-span-12 max-w-2xl text-xl leading-normal md:col-span-7">
                    <Markdown>{item.fields.text}</Markdown>
                  </div>
                )}
              </div>
            );
          } else if (isModuleListSection(item)) {
            return (
              <div key={item.sys.id} className="bg-brand-500 text-white">
                <div
                  key={item.sys.id}
                  className="container flex flex-col gap-10 py-12 md:grid md:md:grid md:grid-cols-12 md:py-32">
                  <div className="col-span-12 lg:col-span-5">
                    <Markdown>{item?.fields?.header?.fields?.header}</Markdown>
                  </div>
                  <ul className="col-span-12 grid gap-10 md:grid-cols-2 lg:col-span-7">
                    {/* @ts-expect-error contentful types are wrong */}
                    {item?.fields?.listItems?.map((listItem) => (
                      <li key={listItem.sys.id} className="flex flex-col gap-5 border-t pt-5">
                        <h3 className="text-3xl">{listItem.fields.header}</h3>
                        <p className="text-xl leading-normal">{listItem.fields.text}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          }
        })}
        {expert.username && <BookingSection booking={booking} userList={[expert.username]} />}
        <ExpertsList
          experts={otherExperts}
          header="Other experts"
          booking={booking}
          expertsNextAvailableSlot={expertsNextAvailableSlot}
        />
      </main>
    </>
  );
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const slug = Array.isArray(context?.params?.slug) ? context?.params?.slug.pop() : context?.params?.slug;

  if (!slug) return { notFound: true } as { notFound: true };

  try {
    const { expert, booking, experts } = await fetchExpertAndBookingData(slug);
    const expertsList = experts.map((e) => e.username);
    const slots = await fetchScheduleData(expertsList, booking);

    return {
      props: {
        expert,
        booking,
        experts,
        slots,
      },
      revalidate: 60,
    };
  } catch (error) {
    console.error(`Error fetching data in getStaticProps on slug "${slug}":`, error);
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    };
  }
};

export const getStaticPaths = async () => {
  const experts = await getExperts();

  const paths = experts
    .filter((expert) => expert.username)
    .map((expert) => ({
      params: {
        slug: expert.username,
      },
    }));

  return {
    paths,
    fallback: "blocking",
  };
};

export default ExpertPage;
